import { useEffect, useState } from 'react';

import { useCategoriesStore } from '@/features/categories/store';
import { useGroupsStore } from '@/features/groups/store';
import { useLedgerStore } from '@/features/ledger/store';

export type BootstrapState = {
  ready: boolean;
  error: string | null;
  retry: () => void;
};

/** Fills the global stores from the API before the app renders. */
export function useBootstrap(): BootstrapState {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await Promise.all([
          useGroupsStore.getState().load(),
          useCategoriesStore.getState().load(),
        ]);

        if (cancelled) return;
        setError(null);
        setReady(true);
      } catch (cause) {
        if (!cancelled) setError((cause as Error).message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  return {
    ready,
    error,
    retry: () => {
      setError(null);
      setAttempt((value) => value + 1);
    },
  };
}

/** Keeps the ledger store pointed at whichever group is selected. */
export function useActiveGroupSync(): void {
  const activeGroupId = useGroupsStore((state) => state.activeGroupId);
  const loadLedger = useLedgerStore((state) => state.load);

  useEffect(() => {
    if (activeGroupId) {
      loadLedger(activeGroupId);
    } else {
      useLedgerStore.setState({
        groupId: null,
        members: [],
        allExpenses: [],
        expenses: [],
        participants: [],
        settlements: [],
      });
    }
  }, [activeGroupId, loadLedger]);
}
